import { addHours, differenceInHours, fromUnixTime } from 'date-fns';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { datadogLogs } from '@datadog/browser-logs';
import { faBellOn } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AnalyticsContext, ObjectTypes } from 'src/AnalyticsContext';
import { useSageAuth } from 'src/auth/useSageAuth';
import DrawerBody from 'src/components/drawers/DrawerBody';
import DrawerContainer from 'src/components/drawers/DrawerContainer';
import DrawerFooter from 'src/components/drawers/DrawerFooter';
import DrawerTitle from 'src/components/drawers/DrawerTitle';
import { AutoOpenDrawer, NotificationResults } from 'src/constants/AutoOpenDrawers';
import { ServiceWorkerMessageTypes } from 'src/constants/ServiceWorkerMessageTypes';
import useEnvironment from 'src/hooks/useEnvironment';
import usePageVisibility from 'src/hooks/usePageVisibility';
import { selectFacility, selectStaffMemberId } from 'src/store/configSlice';
import {
    dismissAutoOpenDrawer,
    requestAutoOpenDrawer,
    selectActiveAutoOpenDrawer,
} from 'src/store/globalUISlice';
import {
    useCreateOrUpdateWebPushNotificationSubscriptionMutation,
    useDeleteWebPushNotificationSubscriptionsMutation,
    useLazyFindWebPushNotificationSubscriptionsQuery,
} from 'src/store/sageMessageOrchestrationApi';
import { isMobilePhone } from 'src/utils/userAgentUtils';

const DefaultSubscriptionExpirationHours = 8;

export const PushNotificationDeliveryMethod = 'PUSH_NOTIFICATION';

export type NotificationsContextValuesType = {
    areNotificationsSupported: boolean;
    areUserPushNotificationsEnabled: boolean;
    deletePushSubscription: () => Promise<boolean>;
    dismissNotificationsByTag: (tag?: string) => void;
    shouldLoadNotificationsContext: boolean;
    subscribeToPush: () => Promise<boolean>;
    unsubscribeFromPush: (staffMemberId: string, unsubscribeFromAll?: boolean) => void;
    notificationsPermission?: NotificationPermission;
    pushSubscriptionEndpoint?: string;
};

export const NotificationsContext = createContext<NotificationsContextValuesType>({
    areNotificationsSupported: false,
    areUserPushNotificationsEnabled: false,
    deletePushSubscription: () => Promise.resolve(false),
    dismissNotificationsByTag: (_taskId?: string) => {},
    shouldLoadNotificationsContext: false,
    subscribeToPush: () => Promise.resolve(false),
    unsubscribeFromPush: (_staffMemberId: string, _unsubscribeFromAll = false) => {},
    notificationsPermission: undefined,
    pushSubscriptionEndpoint: undefined,
});

/**
 * First decode the vapid key, which is already base 64 encoded, then encode into Uint8
 * @param {string} vapidPublicKey
 * @returns
 */
const urlB64ToUint8Array = (vapidPublicKey: string) => {
    const padding = '='.repeat((4 - (vapidPublicKey.length % 4)) % 4);
    const base64 = (vapidPublicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

const getOrCreateServiceWorkerRegistration = async () => {
    try {
        if (!('serviceWorker' in navigator)) {
            datadogLogs.logger.warn('Service workers are not supported by this browser');

            return;
        }

        return (
            (await navigator.serviceWorker.getRegistration()) ??
            (await navigator.serviceWorker.register('/sw.ts'))
        );
    } catch (error: any) {
        datadogLogs.logger.error('Error registering service worker', undefined, error);
        return;
    }
};

/**
 * Gets the number of seconds since the given timestamp
 *
 * @param {string} timestamp
 * @returns {number | undefined}
 */
const getSecondsSinceTimestamp = (timestamp: string) => {
    if (!timestamp) {
        return undefined;
    }

    const createTimestamp = Date.parse(timestamp);
    const now = Date.now();
    return isNaN(createTimestamp) ? undefined : (now - createTimestamp) / 1000;
};

export const AnalyticsPrefix = 'notifications';

const areNotificationsSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

function NotificationsProvider(props: PropsWithChildren) {
    const { trackEvent } = useContext(AnalyticsContext);

    const { getIdTokenExpiry } = useSageAuth();
    const { palette } = useTheme();

    const { isE2E } = useEnvironment();

    const facility = useSelector(selectFacility);
    const staffMemberId = useSelector(selectStaffMemberId);
    const activeAutoOpenDrawer = useSelector(selectActiveAutoOpenDrawer);
    const dispatch = useDispatch();
    const facilityId = facility?.id;

    const isVisible = usePageVisibility();

    const [notificationsPermission, setNotificationsPermission] = useState<
        NotificationPermission | undefined
    >();
    const [notificationSubscription, setNotificationSubscription] = useState<
        PushSubscription | undefined
    >();

    const [getStaffMemberSubscriptionsForCurrentDevice] =
        useLazyFindWebPushNotificationSubscriptionsQuery();
    const [createOrUpdatePushNotificationSubscription] =
        useCreateOrUpdateWebPushNotificationSubscriptionMutation();
    const [deleteWebPushNotificationSubscription] =
        useDeleteWebPushNotificationSubscriptionsMutation();

    const dismissNotificationsByTag = useCallback(
        async (tag?: string) => {
            try {
                const swRegistration = await getOrCreateServiceWorkerRegistration();

                if (swRegistration?.active) {
                    const notifications = await swRegistration.getNotifications({ tag });
                    notifications?.forEach((element) => element.close());
                }
            } catch (error: any) {
                datadogLogs.logger.error(
                    'Error dismissing notifications by tag',
                    { staffMemberId, facilityId, tag },
                    error
                );
            } finally {
                return;
            }
        },
        [facilityId, staffMemberId]
    );

    const subscribeToPush = useCallback(async () => {
        try {
            const swRegistration = await getOrCreateServiceWorkerRegistration();

            if (!swRegistration) {
                datadogLogs.logger.warn(
                    'Service worker registration failed during push subscription'
                );
                return false;
            }

            // Check or create a new subscription
            let subscription = await swRegistration.pushManager?.getSubscription();
            if (!subscription) {
                subscription = await swRegistration.pushManager?.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlB64ToUint8Array(import.meta.env.VITE_VAPID_PUBLIC_KEY),
                });

                datadogLogs.logger.info('Push subscription created', {
                    subscriptionJson: subscription?.toJSON(),
                });
            }

            setNotificationSubscription(subscription);
            if (subscription) {
                datadogLogs.logger.info('Setting push subscription', {
                    subscriptionJson: subscription?.toJSON(),
                });
            } else {
                datadogLogs.logger.warn('No push subscription found');
            }
            return Boolean(subscription);
        } catch (error: any) {
            datadogLogs.logger.error(
                'Error subscribing to push notifications',
                { staffMemberId, facilityId, notificationsPermission: Notification.permission },
                error
            );
            return false;
        }
    }, [facilityId, staffMemberId]);

    /**
     * Deletes this device's push subscription from both the backend and the browser, so the next
     * subscribeToPush creates a brand-new subscription (new endpoint and keys) rather than reusing it.
     */
    const deletePushSubscription = useCallback(async () => {
        try {
            const swRegistration = await getOrCreateServiceWorkerRegistration();
            const subscription = await swRegistration?.pushManager?.getSubscription();

            if (subscription) {
                await deleteWebPushNotificationSubscription({
                    recipientIds: [staffMemberId],
                    endpoints: [subscription.endpoint],
                }).unwrap();
                await subscription.unsubscribe();

                datadogLogs.logger.info('Push subscription deleted', {
                    endpoint: subscription.endpoint,
                });
            }

            setNotificationSubscription(undefined);
            return true;
        } catch (error: any) {
            datadogLogs.logger.error(
                'Error deleting push subscription',
                { staffMemberId, facilityId },
                error
            );
            return false;
        }
    }, [deleteWebPushNotificationSubscription, facilityId, staffMemberId]);

    /**
     * Unsubscribes the user from push notifications.  If unsubscribeFromAll is true, will
     * unsubscribe from all devices instead of just the current one.
     */
    const unsubscribeFromPush = useCallback(
        async (staffMemberId: string, unsubscribeFromAll = false) => {
            try {
                const swRegistration = await getOrCreateServiceWorkerRegistration();
                const subscription = await swRegistration?.pushManager.getSubscription();
                const endpoints = subscription ? [subscription.endpoint] : undefined;

                await deleteWebPushNotificationSubscription({
                    recipientIds: [staffMemberId],
                    endpoints: unsubscribeFromAll ? undefined : endpoints,
                }).unwrap();
            } catch (error: any) {
                datadogLogs.logger.error(
                    'Error unsubscribing from push notifications',
                    { staffMemberId, facilityId },
                    error
                );
            }
        },
        [deleteWebPushNotificationSubscription, facilityId]
    );

    const requestPermissions = async () => {
        try {
            const permissionResult = await Notification.requestPermission();

            if (permissionResult === NotificationResults.Granted) {
                datadogLogs.logger.info('Notifications permissions allowed by user', {
                    permissionResult,
                });
            } else {
                datadogLogs.logger.warn('Notifications permissions denied by user', {
                    permissionResult,
                });
            }

            trackEvent('Notification Permission Request Acknowledged', {
                objectType: ObjectTypes.Dialog,
                permissionResult,
            });

            setNotificationsPermission(permissionResult);
        } catch (error: any) {
            datadogLogs.logger.error(
                'Error requesting permissions',
                { staffMemberId, facilityId, userAgent: navigator.userAgent },
                error
            );
        }
    };

    const shouldLoadNotificationsContext = useMemo(
        () => Boolean(facility?.messageDeliveryMethods?.includes(PushNotificationDeliveryMethod)),
        [facility]
    );

    const isNotificationsDrawerOpen =
        shouldLoadNotificationsContext && activeAutoOpenDrawer === AutoOpenDrawer.Notifications;

    useEffect(() => {
        if (shouldLoadNotificationsContext && areNotificationsSupported) {
            setNotificationsPermission(Notification.permission);

            const handlePermissionChange = (event: Event) => {
                const permissionStatus = event.target as PermissionStatus;
                switch (permissionStatus.state) {
                    case 'granted':
                        setNotificationsPermission('granted');
                        break;
                    case 'denied':
                        setNotificationsPermission('denied');
                        break;
                    case 'prompt':
                        setNotificationsPermission('default');
                        break;
                    default:
                        break;
                }
            };

            // Add permission change listener
            if ('permissions' in navigator) {
                navigator.permissions.query({ name: 'notifications' }).then((permissionStatus) => {
                    permissionStatus.addEventListener('change', handlePermissionChange);
                });
            }

            return () => {
                if ('permissions' in navigator) {
                    navigator.permissions
                        .query({ name: 'notifications' })
                        .then((permissionStatus) => {
                            permissionStatus.removeEventListener('change', handlePermissionChange);
                        });
                }
            };
        } else if (shouldLoadNotificationsContext === false && !areNotificationsSupported) {
            datadogLogs.logger.warn('Notifications are not supported by this browser');
        }
    }, [shouldLoadNotificationsContext, isVisible]);

    useEffect(() => {
        if (
            areNotificationsSupported &&
            shouldLoadNotificationsContext &&
            staffMemberId &&
            !isE2E
        ) {
            getOrCreateServiceWorkerRegistration().then((registration) => {
                if (
                    notificationsPermission === NotificationResults.Default &&
                    registration?.active
                ) {
                    registration.pushManager.getSubscription().then((subscription) => {
                        if (
                            // check for existing push notification subscription in addition to permission is default
                            // since iOS permissions seem to briefly reset on redirecting back to the app
                            // see also https://stackoverflow.com/questions/76590928/pwa-on-ios-notification-permission-return-default-whatever-we-chose
                            !subscription
                        ) {
                            dispatch(requestAutoOpenDrawer(AutoOpenDrawer.Notifications));
                        }
                    });
                } else if (notificationsPermission === NotificationResults.Granted) {
                    datadogLogs.logger.info(
                        'Notification permission granted, attempting push subscription',
                        { notificationsPermission, userAgent: navigator.userAgent }
                    );
                    subscribeToPush();
                }
            });
        }
    }, [
        dispatch,
        isE2E,
        notificationsPermission,
        shouldLoadNotificationsContext,
        staffMemberId,
        subscribeToPush,
    ]);

    useEffect(() => {
        const handleServiceWorkerMessage = async (event: MessageEvent) => {
            try {
                const { type, payload } = event.data;
                const now = new Date().toISOString();

                if (type === ServiceWorkerMessageTypes.NotificationClicked) {
                    const { notification } = payload;
                    const { data, timestamp: notificationEventTimestamp } = notification || {};
                    const { notificationCreateTime } = data || {};

                    const secondsSinceCreation = getSecondsSinceTimestamp(notificationCreateTime);

                    trackEvent('Notification Clicked', {
                        objectType: ObjectTypes.Notification,
                        secondsSinceCreation,
                        now,
                        notificationEventTimestamp,
                        ...data,
                    });
                } else {
                    datadogLogs.logger.warn(`Unknown service worker message type: ${type}`);
                }
            } catch (error: any) {
                datadogLogs.logger.error(
                    'Error handling service worker message',
                    { staffMemberId, facilityId, event },
                    error
                );
            }
        };

        getOrCreateServiceWorkerRegistration().then((serviceWorkerRegistration) => {
            if (serviceWorkerRegistration) {
                navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
            }
        });

        return () => {
            navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, staffMemberId]);

    useEffect(() => {
        if (!notificationSubscription) {
            return;
        }

        const recordSubscription = async () => {
            const existingSubscriptions =
                (
                    await getStaffMemberSubscriptionsForCurrentDevice({
                        recipientIds: [staffMemberId],
                        endpoints: [notificationSubscription.endpoint],
                    }).unwrap()
                )?.subscriptions || [];

            const existingSubscriptionExpiration = existingSubscriptions?.[0]?.expirationTime;
            // push to backend if either no existing or the existing subscription expire time is within 1 hour
            if (
                !existingSubscriptionExpiration ||
                differenceInHours(
                    fromUnixTime(parseInt(existingSubscriptionExpiration)),
                    Date.now()
                ) <= 1
            ) {
                const idTokenExpiry = await getIdTokenExpiry();
                const expirationTime = idTokenExpiry
                    ? fromUnixTime(idTokenExpiry).toISOString()
                    : addHours(Date.now(), DefaultSubscriptionExpirationHours).toISOString();

                const subscriptionAsObject = notificationSubscription.toJSON();
                const { rid: subscriptionId } = await createOrUpdatePushNotificationSubscription({
                    webPushNotificationSubscription: {
                        ...subscriptionAsObject,
                        endpoint: subscriptionAsObject.endpoint!,
                        recipientId: staffMemberId,
                        expirationTime,
                        isMobilePhone: isMobilePhone(),
                    },
                }).unwrap();

                datadogLogs.logger.info(
                    `Subscribed to notifications for user ${staffMemberId} with expirationTime ${expirationTime} for id ${subscriptionId}.`
                );
            }
        };

        recordSubscription().catch((error) =>
            datadogLogs.logger.error(
                'Error recording subscription',
                { staffMemberId, facilityId },
                error
            )
        );
    }, [
        createOrUpdatePushNotificationSubscription,
        facilityId,
        getIdTokenExpiry,
        getStaffMemberSubscriptionsForCurrentDevice,
        notificationSubscription,
        staffMemberId,
    ]);

    const areUserPushNotificationsEnabled =
        areNotificationsSupported &&
        notificationsPermission !== NotificationResults.Denied &&
        shouldLoadNotificationsContext;

    const contextValues: NotificationsContextValuesType = useMemo(
        () => ({
            areNotificationsSupported,
            areUserPushNotificationsEnabled,
            notificationsPermission,
            pushSubscriptionEndpoint: notificationSubscription?.endpoint,
            shouldLoadNotificationsContext,
            ...(areNotificationsSupported && shouldLoadNotificationsContext
                ? {
                      deletePushSubscription,
                      dismissNotificationsByTag,
                      subscribeToPush,
                      unsubscribeFromPush,
                  }
                : {
                      deletePushSubscription: () => Promise.resolve(false),
                      dismissNotificationsByTag: () => {},
                      subscribeToPush: () => Promise.resolve(false),
                      unsubscribeFromPush: () => {},
                  }),
        }),
        [
            areUserPushNotificationsEnabled,
            deletePushSubscription,
            dismissNotificationsByTag,
            notificationSubscription,
            notificationsPermission,
            shouldLoadNotificationsContext,
            subscribeToPush,
            unsubscribeFromPush,
        ]
    );

    const handleCloseDrawer = () => {
        requestPermissions();
        dispatch(dismissAutoOpenDrawer(AutoOpenDrawer.Notifications));
    };

    return (
        <NotificationsContext.Provider value={contextValues}>
            {props.children}
            <DrawerContainer
                anchor="bottom"
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        // we want this to explicitly be controlled by the `open` prop
                        // see https://mui.com/material-ui/migration/v5-component-changes/#%E2%9C%85-remove-disablebackdropclick-prop
                        return;
                    }
                }}
                open={isNotificationsDrawerOpen}
            >
                <DrawerTitle />

                <DrawerBody sx={{ alignItems: 'flex-start', gap: 2 }}>
                    <FontAwesomeIcon icon={faBellOn} color={palette.primary.main} size="2x" />
                    <Stack sx={{ alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            Allow notifications
                        </Typography>
                        <Typography variant="body2">
                            You must accept the browser notifications on the following screen to
                            enable this phone to receive alerts.
                        </Typography>
                    </Stack>
                </DrawerBody>

                <DrawerFooter>
                    <Button
                        disableElevation
                        fullWidth
                        variant="contained"
                        onClick={handleCloseDrawer}
                    >
                        Got It
                    </Button>
                </DrawerFooter>
            </DrawerContainer>
        </NotificationsContext.Provider>
    );
}

export default NotificationsProvider;
